import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export function useCart() {
  return useContext(CartContext);
}

export function CartProvider({ children }) {
  // Try to load cart from localStorage
  const [cartItems, setCartItems] = useState(() => {
    try {
      const saved = localStorage.getItem('cravedash_cart');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });

  const [cartCompany, setCartCompany] = useState(() => {
    try {
      const saved = localStorage.getItem('cravedash_cart_company');
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      return null;
    }
  });

  // Save to localStorage when changed
  useEffect(() => {
    localStorage.setItem('cravedash_cart', JSON.stringify(cartItems));
    localStorage.setItem('cravedash_cart_company', JSON.stringify(cartCompany));
  }, [cartItems, cartCompany]);

  const addToCart = (product, company, quantity = 1, customizations = null) => {
    if (cartCompany && cartCompany.id !== company.id) {
      const confirmClear = window.confirm(
        `Your cart contains items from ${cartCompany.name}. Do you want to clear it and add this item from ${company.name}?`
      );
      if (!confirmClear) return false; // Aborted
      
      // Clear cart
      setCartItems([]);
      setCartCompany(company);
    } else if (!cartCompany) {
      setCartCompany(company);
    }

    setCartItems(prev => {
      // Simple check if same product (could be expanded to check customizations)
      const existingIdx = prev.findIndex(item => item.productId === product.id && JSON.stringify(item.customizations) === JSON.stringify(customizations));
      if (existingIdx >= 0) {
        const newItems = [...prev];
        newItems[existingIdx].quantity += quantity;
        return newItems;
      }
      
      return [...prev, {
        productId: product.id,
        name: product.name || product.title,
        price: parseFloat(product.finalPrice || product.price || product.basePrice || 0),
        quantity,
        image: product.images && product.images.length > 0 ? product.images[0] : null,
        category: product.category,
        customizations: customizations
      }];
    });
    
    return true; // Success
  };

  const removeFromCart = (index) => {
    setCartItems(prev => {
      const newItems = prev.filter((_, i) => i !== index);
      if (newItems.length === 0) {
        setCartCompany(null);
      }
      return newItems;
    });
  };

  const updateQuantity = (index, newQuantity) => {
    if (newQuantity < 1) return;
    setCartItems(prev => {
      const newItems = [...prev];
      newItems[index].quantity = newQuantity;
      return newItems;
    });
  };

  const clearCart = () => {
    setCartItems([]);
    setCartCompany(null);
  };

  const cartTotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  const value = {
    cartItems,
    cartCompany,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    cartTotal,
    cartCount: cartItems.reduce((sum, item) => sum + item.quantity, 0)
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}
